/**
 * A single view toggle (the balance card's ledger section) remembered across
 * reloads — the same localStorage pattern the report's group set uses: read
 * tolerantly, write in an effect, survive a storage that is not there.
 *
 * View state, not a filter: it lives out of the URL on purpose, so a shared
 * link never carries an opinion about how somebody else's screen was folded.
 */

import { useCallback, useEffect, useState } from 'react';

/** `null`/unreadable storage falls back to the default for the session. */
export function readStoredFlag(key: string, fallback: boolean): boolean {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return fallback;
  } catch {
    return fallback;
  }
}

type FlagUpdater = boolean | ((current: boolean) => boolean);

export function usePersistedFlag(key: string, fallback: boolean): [boolean, (next: FlagUpdater) => void] {
  const [value, setValue] = useState<boolean>(() => readStoredFlag(key, fallback));

  // One writer, one effect: no side effects inside the state updater, which
  // StrictMode is free to call twice.
  useEffect(() => {
    try {
      window.localStorage.setItem(key, String(value));
    } catch {
      // Unpersistable is not unusable — keep the in-memory state.
    }
  }, [key, value]);

  const update = useCallback((next: FlagUpdater) => {
    setValue((current) => (typeof next === 'function' ? next(current) : next));
  }, []);

  return [value, update];
}
